import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Save, Search, CheckSquare, Square, GraduationCap, School,
  Calendar, Users, Loader2, ArrowRight, RotateCcw, Filter,
  RefreshCw, FileText, Printer, Download, AlertTriangle
} from "lucide-react";
import { apiService } from "@/services/api";
import { useAuthStore } from "@/store/authStore";
import { toast } from "sonner";
import { useLang } from "@/hooks/useLang";

const t = {
  fr: {
    title: "Inscription des anciens élèves",
    subtitle: "Réinscription selon les décisions du conseil de classe",
    school: "École",
    sourceYear: "Année Conseil",
    sourceClass: "Classe Conseil",
    targetYear: "Nouvelle Année",
    targetClass: "Nouvelle Classe",
    regime: "Nouveau Régime",
    options: "Nouvelles Options",
    studentList: "Liste des élèves",
    matricule: "Matricule",
    name: "Nom Complet",
    gender: "Sx",
    age: "Âge",
    country: "Pays",
    repeating: "Red",
    status: "Statut",
    regimeCol: "Régime",
    councilDecision: "Décision Conseil",
    nextClass: "Classe Suivante",
    selectAll: "Tout sélectionner",
    clearSelection: "Effacer",
    invertSelection: "Inverser",
    enroll: "Inscrire les sélectionnés",
    enrolling: "Inscription en cours...",
    success: "Succès",
    error: "Erreur",
    loading: "Chargement...",
    noData: "Aucun élève trouvé",
    normal: "Normal",
    boursier: "Boursier",
    externe: "Externe",
    demiPension: "Demi-pensionnaire",
    interne: "Interne",
    print: "Imprimer",
    export: "Exporter",
    filterAdmis: "Admis",
    filterRedouble: "Redoublants",
    filterExclu: "Exclus",
    filterAll: "Tous",
    enrolled: "inscrits",
    students: "élèves",
    avg: "Moy.",
    distinctions: "Distinctions",
    autoTarget: "Classe cible auto",
    noNextClass: "Aucune classe proposée",
    proposal: "Proposition",
    promoted: "Promu en",
    repeatClass: "Redouble en",
    excluded: "Exclu",
    councilResult: "Résultat Conseil",
  },
  en: {
    title: "Former Students Enrollment",
    subtitle: "Re-enrollment based on class council decisions",
    school: "School",
    sourceYear: "Council Year",
    sourceClass: "Council Class",
    targetYear: "New Year",
    targetClass: "New Class",
    regime: "New Regime",
    options: "New Options",
    studentList: "Student List",
    matricule: "ID",
    name: "Full Name",
    gender: "Gender",
    age: "Age",
    country: "Country",
    repeating: "Rep",
    status: "Status",
    regimeCol: "Regime",
    councilDecision: "Council Decision",
    nextClass: "Next Class",
    selectAll: "Select all",
    clearSelection: "Clear",
    invertSelection: "Invert",
    enroll: "Enroll selected",
    enrolling: "Enrolling...",
    success: "Success",
    error: "Error",
    loading: "Loading...",
    noData: "No students found",
    normal: "Normal",
    boursier: "Scholarship",
    externe: "Day student",
    demiPension: "Half-board",
    interne: "Boarder",
    print: "Print",
    export: "Export",
    filterAdmis: "Admitted",
    filterRedouble: "Repeating",
    filterExclu: "Excluded",
    filterAll: "All",
    enrolled: "enrolled",
    students: "students",
    avg: "Avg",
    distinctions: "Distinctions",
    autoTarget: "Auto target class",
    noNextClass: "No class proposed",
    proposal: "Proposal",
    promoted: "Promoted to",
    repeatClass: "Repeat in",
    excluded: "Excluded",
    councilResult: "Council Result",
  }
};

interface StudentEnrollment {
  student_id: number;
  matricule: string;
  full_name: string;
  gender: string;
  age: number;
  country: string;
  is_repeating: boolean;
  enrollment_status: string;
  regime: string;
  council_decision: string;
  next_class_id: number | null;
  next_class_name: string | null;
  council_repeating: boolean;
  general_average: number;
  tableau_honneur: boolean;
  encouragements: boolean;
  felicitations: boolean;
  avertissement_travail: boolean;
  blame_travail: boolean;
  avertissement_conduite: boolean;
  blame_conduite: boolean;
}

export default function ClassCouncilEnrollmentPage() {
  const { lang } = useLang();
  const txt = t[lang];
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [enrolling, setEnrolling] = useState(false);
  const [academicYears, setAcademicYears] = useState<any[]>([]);
  const [classes, setClasses] = useState<any[]>([]);
  const [students, setStudents] = useState<StudentEnrollment[]>([]);
  const [selectedSourceYear, setSelectedSourceYear] = useState("");
  const [selectedSourceClass, setSelectedSourceClass] = useState("");
  const [selectedTargetYear, setSelectedTargetYear] = useState("");
  const [selectedTargetClass, setSelectedTargetClass] = useState("");
  const [selectedRegime, setSelectedRegime] = useState("Normal");
  const [selectedStudents, setSelectedStudents] = useState<Set<number>>(new Set());
  const [filterDecision, setFilterDecision] = useState<"all" | "admis" | "redouble" | "exclu">("all");
  const [searchQuery, setSearchQuery] = useState("");

  const schoolId = user?.school_id || 1;

  const loadData = useCallback(async () => {
    if (!schoolId) return;
    try {
      let years: any[] = [];
      try {
        const yearRes = await apiService.get(`/schools/${schoolId}/academic-years`);
        console.log("[Enrollment] years response:", yearRes);
        years = Array.isArray(yearRes) ? yearRes : (yearRes as any)?.data || (yearRes as any)?.items || [];
        console.log("[Enrollment] years loaded:", years.length);
      } catch (e: any) {
        console.error("[Enrollment] years error:", e?.response?.data || e?.message || e);
      }
      setAcademicYears(years);

      let cls: any[] = [];
      try {
        const clsRes = await apiService.get("/classes", { params: { school_id: schoolId, per_page: 1000 } });
        console.log("[Enrollment] classes response:", clsRes);
        cls = Array.isArray(clsRes) ? clsRes : (clsRes as any)?.data || (clsRes as any)?.items || [];
        console.log("[Enrollment] classes loaded:", cls.length);
      } catch (e: any) {
        console.error("[Enrollment] classes error:", e?.response?.data || e?.message || e);
      }
      setClasses(cls);
    } catch (e) { toast.error(txt.error); }
  }, [schoolId, txt.error]);

  useEffect(() => { loadData(); }, [loadData]);

  const loadStudents = useCallback(async () => {
    if (!selectedSourceYear || !selectedSourceClass) return;
    setLoading(true);
    try {
      const res = await apiService.get("/class-council/enrollment/source-students", {
        params: {
          source_academic_year_id: parseInt(selectedSourceYear),
          source_class_id: parseInt(selectedSourceClass),
        }
      });
      const data = (res as any)?.data || res;
      if (data?.students) {
        setStudents(data.students);
        setSelectedStudents(new Set());
      }
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    } finally {
      setLoading(false);
    }
  }, [selectedSourceYear, selectedSourceClass, txt.error]);

  useEffect(() => {
    if (selectedSourceYear && selectedSourceClass) loadStudents();
  }, [selectedSourceYear, selectedSourceClass, loadStudents]);

  const filteredStudents = useMemo(() => {
    let list = [...students];
    if (filterDecision === "admis") {
      list = list.filter(s => s.council_decision && (s.council_decision.includes("Admi") || s.council_decision.includes("ADMIS")));
    } else if (filterDecision === "redouble") {
      list = list.filter(s => s.council_decision && (s.council_decision.includes("Redoubl") || s.council_decision.includes("REDOUBLE")));
    } else if (filterDecision === "exclu") {
      list = list.filter(s => s.council_decision && (s.council_decision.includes("Exclu") || s.council_decision.includes("EXCLU")));
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s => s.full_name.toLowerCase().includes(q) || s.matricule.toLowerCase().includes(q));
    }
    return list;
  }, [students, filterDecision, searchQuery]);

  // V14: Auto-détecter la classe cible la plus fréquente parmi les sélectionnés
  const proposedTargetClass = useMemo(() => {
    const counts: Record<number, number> = {};
    filteredStudents.forEach(s => {
      if (s.next_class_id) {
        counts[s.next_class_id] = (counts[s.next_class_id] || 0) + 1;
      }
    });
    const sorted = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    return sorted.length > 0 ? String(sorted[0][0]) : "";
  }, [filteredStudents]);

  // V14: Auto-sélectionner la classe cible proposée
  useEffect(() => {
    if (proposedTargetClass && !selectedTargetClass) {
      setSelectedTargetClass(proposedTargetClass);
    }
  }, [proposedTargetClass, selectedTargetClass]);

  const toggleStudent = (id: number) => {
    setSelectedStudents(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedStudents(new Set(filteredStudents.map(s => s.student_id)));
  const clearAll = () => setSelectedStudents(new Set());
  const invertSelection = () => {
    setSelectedStudents(prev => {
      const next = new Set<number>();
      filteredStudents.forEach(s => {
        if (!prev.has(s.student_id)) next.add(s.student_id);
      });
      return next;
    });
  };

  const handleEnroll = async () => {
    if (selectedStudents.size === 0) {
      toast.error(lang === 'fr' ? 'Sélectionnez au moins un élève' : 'Select at least one student');
      return;
    }
    if (!selectedTargetYear) {
      toast.error(lang === 'fr' ? "Sélectionnez la nouvelle année scolaire" : "Select the new academic year");
      return;
    }

    setEnrolling(true);
    try {
      const res = await apiService.post("/class-council/enrollment/execute", {
        academic_year_id: parseInt(selectedTargetYear),
        source_academic_year_id: parseInt(selectedSourceYear),
        source_class_id: parseInt(selectedSourceClass),
        target_class_id: selectedTargetClass ? parseInt(selectedTargetClass) : null,
        student_ids: Array.from(selectedStudents),
        regime: selectedRegime,
      });
      toast.success(`${(res as any)?.message || txt.success}`);
      setSelectedStudents(new Set());
      loadStudents();
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || txt.error);
    } finally {
      setEnrolling(false);
    }
  };

  const getDecisionColor = (decision: string) => {
    if (!decision) return "bg-gray-100 text-gray-600";
    const d = decision.toUpperCase();
    if (d.includes("ADMI")) return "bg-emerald-100 text-emerald-700";
    if (d.includes("RED")) return "bg-amber-100 text-amber-700";
    if (d.includes("EXC")) return "bg-red-100 text-red-700";
    return "bg-gray-100 text-gray-600";
  };

  const getDistinctions = (s: StudentEnrollment) => {
    const badges = [];
    if (s.tableau_honneur) badges.push("TH");
    if (s.encouragements) badges.push("Enc");
    if (s.felicitations) badges.push("Félic");
    if (s.avertissement_travail) badges.push("Avert.T");
    if (s.blame_travail) badges.push("Blâme T");
    if (s.avertissement_conduite) badges.push("Avert.C");
    if (s.blame_conduite) badges.push("Blâme C");
    return badges;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <GraduationCap className="w-7 h-7 text-[#1e3a8a]" />
          {txt.title}
        </h1>
        <p className="text-sm text-gray-500 mt-1">{txt.subtitle}</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* LEFT: Configuration */}
        <div className="xl:col-span-1 space-y-4">
          {/* Source */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <School className="w-4 h-4 text-[#1e3a8a]" /> Source
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.sourceYear} *</label>
                <select value={selectedSourceYear} onChange={e => setSelectedSourceYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.name || y.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.sourceClass} *</label>
                <select value={selectedSourceClass} onChange={e => setSelectedSourceClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {classes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Target */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 flex items-center gap-2 mb-4">
              <ArrowRight className="w-4 h-4 text-[#1e3a8a]" /> Cible
            </h3>
            <div className="space-y-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.targetYear} *</label>
                <select value={selectedTargetYear} onChange={e => setSelectedTargetYear(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {academicYears.map(y => <option key={y.id} value={String(y.id)}>{y.name || y.label}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.targetClass} {proposedTargetClass && <span className="text-emerald-600 font-medium">({txt.autoTarget})</span>}</label>
                <select value={selectedTargetClass} onChange={e => setSelectedTargetClass(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="">{lang === 'fr' ? 'Sélectionner...' : 'Select...'}</option>
                  {classes.map(c => <option key={c.id} value={String(c.id)}>{c.name}</option>)}
                </select>
                {proposedTargetClass && !selectedTargetClass && (
                  <p className="text-xs text-emerald-600 mt-1">{txt.proposal}: {classes.find(c => String(c.id) === proposedTargetClass)?.name || proposedTargetClass}</p>
                )}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{txt.regime}</label>
                <select value={selectedRegime} onChange={e => setSelectedRegime(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-[#1e3a8a]">
                  <option value="Normal">{txt.normal}</option>
                  <option value="Boursier">{txt.boursier}</option>
                  <option value="Externe">{txt.externe}</option>
                  <option value="Demi-pension">{txt.demiPension}</option>
                  <option value="Interne">{txt.interne}</option>
                </select>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
            <button
              onClick={handleEnroll}
              disabled={enrolling || selectedStudents.size === 0}
              className="w-full px-4 py-3 bg-[#1e3a8a] text-white rounded-lg text-sm font-medium hover:bg-[#152a5e] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {enrolling ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              {enrolling ? txt.enrolling : `${txt.enroll} (${selectedStudents.size})`}
            </button>
            <p className="text-xs text-gray-500 mt-2 text-center">
              {selectedStudents.size} {txt.students} {txt.enrolled}
            </p>
          </div>
        </div>

        {/* RIGHT: Student list */}
        <div className="xl:col-span-2 space-y-4">
          {/* Toolbar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <div className="flex gap-2">
              <button onClick={selectAll} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.selectAll}</button>
              <button onClick={clearAll} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.clearSelection}</button>
              <button onClick={invertSelection} className="px-2 py-1 bg-gray-100 rounded text-xs hover:bg-gray-200">{txt.invertSelection}</button>
            </div>
            <div className="h-6 w-px bg-gray-200" />
            <div className="flex gap-2 flex-wrap">
              <button onClick={() => setFilterDecision("all")} className={`px-3 py-1 rounded text-xs ${filterDecision === "all" ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-700'}`}>
                {txt.filterAll}
              </button>
              <button onClick={() => setFilterDecision("admis")} className={`px-3 py-1 rounded text-xs ${filterDecision === "admis" ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {txt.filterAdmis}
              </button>
              <button onClick={() => setFilterDecision("redouble")} className={`px-3 py-1 rounded text-xs ${filterDecision === "redouble" ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {txt.filterRedouble}
              </button>
              <button onClick={() => setFilterDecision("exclu")} className={`px-3 py-1 rounded text-xs ${filterDecision === "exclu" ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'}`}>
                {txt.filterExclu}
              </button>
            </div>
            <div className="flex-1" />
            <div className="relative">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" placeholder={lang === 'fr' ? 'Rechercher...' : 'Search...'}
                value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                className="pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm w-48" />
            </div>
            <button onClick={loadStudents} className="p-2 hover:bg-gray-100 rounded-lg text-gray-600">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-3 py-3 w-10"></th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{txt.matricule}</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{txt.name}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-10">{txt.gender}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-12">{txt.age}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-16">{txt.avg}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase w-24">{txt.distinctions}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{txt.councilDecision}</th>
                    <th className="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase">{txt.nextClass}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <Loader2 className="w-6 h-6 animate-spin mx-auto" /> {txt.loading}
                    </td></tr>
                  ) : filteredStudents.length === 0 ? (
                    <tr><td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                      <Users className="w-10 h-10 mx-auto mb-2 text-gray-300" /> {txt.noData}
                    </td></tr>
                  ) : (
                    filteredStudents.map(student => (
                      <tr key={student.student_id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-3 py-3">
                          <input type="checkbox" checked={selectedStudents.has(student.student_id)}
                            onChange={() => toggleStudent(student.student_id)}
                            className="w-4 h-4 text-[#1e3a8a] rounded" />
                        </td>
                        <td className="px-3 py-3 text-sm font-mono text-[#1e3a8a]">{student.matricule}</td>
                        <td className="px-3 py-3 text-sm font-medium text-gray-900">{student.full_name}</td>
                        <td className="px-3 py-3 text-center text-sm text-gray-700">{student.gender}</td>
                        <td className="px-3 py-3 text-center text-sm text-gray-700">{student.age}</td>
                        <td className="px-3 py-3 text-center text-sm font-bold text-[#1e3a8a]">
                          {student.general_average > 0 ? student.general_average.toFixed(2) : "-"}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className="flex flex-wrap gap-1 justify-center">
                            {getDistinctions(student).map((badge, i) => (
                              <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                                badge === "TH" ? "bg-amber-100 text-amber-700" :
                                badge === "Félic" ? "bg-cyan-100 text-cyan-700" :
                                badge === "Enc" ? "bg-blue-100 text-blue-700" :
                                badge.startsWith("Avert") ? "bg-orange-100 text-orange-700" :
                                "bg-red-100 text-red-700"
                              }`}>{badge}</span>
                            ))}
                            {getDistinctions(student).length === 0 && <span className="text-gray-300 text-xs">-</span>}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${getDecisionColor(student.council_decision)}`}>
                            {student.council_decision}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-center text-sm text-gray-700">
                          {student.next_class_name ? (
                            <span className="text-emerald-700 font-medium">{student.next_class_name}</span>
                          ) : (
                            <span className="text-gray-400 text-xs">{txt.noNextClass}</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            {filteredStudents.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">{filteredStudents.length} {txt.students}</span>
                <span className="text-xs text-gray-500">{selectedStudents.size} {txt.enrolled}</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}